import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Grid, Modal } from 'semantic-ui-react';
import { SetEstateResponsibleModalProps } from './types';
import { FormContainer, Form, ButtonContainer } from './styles';
import {
  dangerNotification,
  successNotification
} from '../../../../services/notification';
import { getUsers } from '../../../../services/estate';
import { UsersToCustomDropDownOptions } from '../../../../util';
import { updateClaimEstateResponsible } from '../../../../services/claim';
import { UserState } from '../../../../types/UserState';

const SetEstateResponsibleModal: React.FC<SetEstateResponsibleModalProps> = (
  props: SetEstateResponsibleModalProps
) => {
  const { isOpen, onClose, claimId, estateId, responsible, onSuccess } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUsersLoading, setIsUsersLoading] = useState<boolean>(false);
  const [estateUsers, setEstateUsers] = useState<UserState[]>([]);
  const [estateResponsible, setEstateResponsible] = useState<string | null>(
    responsible || null
  );

  useEffect(() => {
    setEstateResponsible(responsible || null);
  }, [responsible, isOpen]);

  useEffect(() => {
    if (!isOpen || !estateId) {
      return;
    }

    const loadUsers = async () => {
      setIsUsersLoading(true);
      try {
        const users = await getUsers(estateId);
        setEstateUsers(users);
      } catch (error) {
        dangerNotification('Ops...', 'Erro ao carregar usuários da imobiliária.');
        setEstateUsers([]);
      } finally {
        setIsUsersLoading(false);
      }
    };

    loadUsers();
  }, [estateId, isOpen]);

  const estateUserOptions = useMemo(() => {
    return UsersToCustomDropDownOptions(
      false,
      estateUsers.map((user) => ({
        id: user.id,
        name: user.name
      }))
    );
  }, [estateUsers]);

  if (!isOpen) {
    return null;
  }

  const handleFormSubmit = async (e: React.FormEvent<HTMLElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await updateClaimEstateResponsible(claimId, estateResponsible);
      if (result.statusCode === 200) {
        successNotification('Sucesso', String(result.message));
        onSuccess?.();
        onClose(e);
      } else {
        dangerNotification('Erro', String(result.message || 'Erro ao atribuir responsável.'));
      }
    } catch (error) {
      dangerNotification('Erro', 'Erro ao atribuir responsável.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} closeIcon>
      <Modal.Header>
        {responsible ? 'Alterar responsável pelo sinistro' : 'Atribuir responsável pelo sinistro'}
      </Modal.Header>
      <Form onSubmit={handleFormSubmit}>
        <FormContainer>
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                <label style={{ paddingBottom: '6px', display: 'block' }}>
                  Responsável da imobiliária:
                </label>
                <Dropdown
                  style={{ width: '100%' }}
                  value={estateResponsible || undefined}
                  name="estateResponsibleId"
                  placeholder="Selecionar..."
                  options={estateUserOptions}
                  disabled={isLoading || isUsersLoading}
                  loading={isUsersLoading}
                  search
                  selection
                  clearable
                  onChange={(e, { value }) => {
                    setEstateResponsible((value as string) || null);
                  }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </FormContainer>
      </Form>
      <Modal.Actions>
        <ButtonContainer>
          <Button type="button" color="red" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            color="green"
            loading={isLoading}
            disabled={isLoading || isUsersLoading}
            onClick={handleFormSubmit}
          >
            Confirmar
          </Button>
        </ButtonContainer>
      </Modal.Actions>
    </Modal>
  );
};

export default SetEstateResponsibleModal;
